import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  Beef,
  Eye,
  EyeOff,
  KeyRound,
  Mail,
  ShieldCheck
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;

const initialForm = {
  email: '',
  password: '',
  full_name: '',
  phone: ''
};

export default function LoginPage({ onLogin }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const resetToken = searchParams.get('reset_token') || '';
  const verifyEmailToken = searchParams.get('verify_email_token') || '';

  const [mode, setMode] = useState(verifyEmailToken ? 'verifying-email-link' : (resetToken ? 'reset' : 'login'));
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(initialForm);
  const [noEmailLogin, setNoEmailLogin] = useState({ full_name: '', phone: '', password: '' });
  const [forgotEmail, setForgotEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [twoFA, setTwoFA] = useState({
    challengeId: '',
    code: '',
    method: '',
    contactHint: ''
  });
  const [emailVerification, setEmailVerification] = useState({
    challengeId: '',
    code: '',
    contactHint: ''
  });

  useEffect(() => {
    if (resetToken) {
      setMode('reset');
    }
  }, [resetToken]);

  useEffect(() => {
    if (!verifyEmailToken) return;

    let cancelled = false;
    const verifyEmail = async () => {
      setMode('verifying-email-link');
      setLoading(true);
      try {
        const response = await axios.get(`${API}/auth/verify-registration-email-link`, {
          params: { token: verifyEmailToken }
        });
        if (cancelled) return;
        setSearchParams({});
        toast.success('Email verificata! Accesso effettuato.');
        onLogin(response.data.user, response.data.token);
        redirectAfterLogin(response.data.user);
      } catch (error) {
        if (cancelled) return;
        setSearchParams({});
        setMode('login');
        toast.error(error.response?.data?.detail || 'Impossibile verificare l\'email');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    verifyEmail();
    return () => { cancelled = true; };
  }, [verifyEmailToken]);

  const isLogin = mode === 'login';
  const isRegister = mode === 'register';

  const subtitle = useMemo(() => {
    if (mode === 'register') return 'Crea il tuo account';
    if (mode === 'login-no-email') return 'Accedi senza email';
    if (mode === 'forgot') return 'Ricevi il link per reimpostare la password';
    if (mode === 'reset') return 'Scegli una nuova password';
    if (mode === '2fa') return 'Inserisci il codice di sicurezza';
    if (mode === 'verify-email') return 'Inserisci il codice ricevuto via email';
    if (mode === 'verifying-email-link') return 'Verifica email in corso';
    return 'Accedi al tuo account';
  }, [mode]);

  const redirectAfterLogin = (user) => {
    if (user.role === 'admin' || user.role === 'employee') {
      navigate('/admin');
    } else {
      navigate('/dashboard');
    }
  };

  const handleAuthSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);

    try {
      const endpoint = isLogin ? '/auth/login' : '/auth/register';
      const payload = isLogin
        ? { email: formData.email, password: formData.password }
        : formData;
      const response = await axios.post(`${API}${endpoint}`, payload);

      if (isLogin && response.data.requires_2fa) {
        setTwoFA({
          challengeId: response.data.challenge_id,
          code: '',
          method: response.data.method || 'email',
          contactHint: response.data.contact_hint || ''
        });
        setMode('2fa');
        toast.info('Codice di verifica inviato');
        return;
      }

      if (response.data.requires_email_verification) {
        setEmailVerification({
          challengeId: response.data.challenge_id || '',
          code: '',
          contactHint: response.data.contact_hint || formData.email
        });
        setMode('verify-email');
        toast.info(response.data.message || 'Controlla la tua email');
        return;
      }

      toast.success(isLogin ? 'Accesso effettuato!' : 'Registrazione completata!');
      onLogin(response.data.user, response.data.token);
      redirectAfterLogin(response.data.user);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante l\'autenticazione');
    } finally {
      setLoading(false);
    }
  };

  const handleNoEmailLogin = async (event) => {
    event.preventDefault();
    if (!noEmailLogin.full_name.trim() || !noEmailLogin.phone.trim() || !noEmailLogin.password) {
      toast.error('Inserisci nome, telefono e password');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login-without-email`, {
        full_name: noEmailLogin.full_name.trim(),
        phone: noEmailLogin.phone.trim(),
        password: noEmailLogin.password
      });
      toast.success('Accesso effettuato!');
      onLogin(response.data.user, response.data.token);
      redirectAfterLogin(response.data.user);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Nome, telefono o password non validi');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyRegistrationEmail = async (event) => {
    event.preventDefault();
    if (emailVerification.code.length !== 6) {
      toast.error('Inserisci il codice di 6 cifre');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/verify-registration-email`, {
        challenge_id: emailVerification.challengeId,
        code: emailVerification.code
      });
      toast.success('Email verificata e registrazione completata!');
      onLogin(response.data.user, response.data.token);
      redirectAfterLogin(response.data.user);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Codice non valido');
    } finally {
      setLoading(false);
    }
  };

  const handleResendRegistrationCode = async () => {
    const email = formData.email.trim();
    if (!email) {
      toast.error('Inserisci nuovamente la tua email');
      setMode('login');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/resend-registration-email-code`, { email });
      setEmailVerification((current) => ({
        ...current,
        challengeId: response.data.challenge_id,
        code: '',
        contactHint: response.data.contact_hint || email
      }));
      toast.success(response.data.message || 'Nuovo codice inviato');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Impossibile reinviare il codice');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2FA = async (event) => {
    event.preventDefault();
    if (twoFA.code.length !== 6) {
      toast.error('Inserisci il codice di 6 cifre');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/2fa/verify-login`, {
        challenge_id: twoFA.challengeId,
        code: twoFA.code
      });
      toast.success('Accesso verificato!');
      onLogin(response.data.user, response.data.token);
      redirectAfterLogin(response.data.user);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Codice non valido');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (event) => {
    event.preventDefault();
    if (!forgotEmail.trim()) {
      toast.error('Inserisci la tua email');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/forgot-password`, {
        email: forgotEmail.trim()
      });
      toast.success(response.data.message || 'Controlla la tua email');
      setMode('login');
      setFormData((current) => ({ ...current, email: forgotEmail.trim() }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Impossibile inviare l\'email di recupero');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (event) => {
    event.preventDefault();

    if (!resetToken) {
      toast.error('Link di recupero non valido');
      return;
    }
    if (newPassword.length < 8) {
      toast.error('La nuova password deve contenere almeno 8 caratteri');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Le password non coincidono');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/reset-password`, {
        token: resetToken,
        new_password: newPassword
      });
      toast.success(response.data.message || 'Password aggiornata');
      setSearchParams({});
      setNewPassword('');
      setConfirmPassword('');
      setMode('login');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Impossibile reimpostare la password');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (event) => {
    setFormData((current) => ({ ...current, [event.target.name]: event.target.value }));
  };

  const backToLogin = () => {
    setMode('login');
    setTwoFA({ challengeId: '', code: '', method: '', contactHint: '' });
    setEmailVerification({ challengeId: '', code: '', contactHint: '' });
    if (resetToken || verifyEmailToken) setSearchParams({});
  };

  return (
    <div className="min-h-screen bg-[#F9F7F2] flex items-center justify-center p-6" data-testid="login-page">
      <div className="absolute inset-0 z-0 opacity-5">
        <img
          src="https://images.pexels.com/photos/30649013/pexels-photo-30649013.jpeg"
          alt="Background"
          className="w-full h-full object-cover"
        />
      </div>

      <div className="max-w-md w-full relative z-10">
        <div className="bg-white rounded-2xl card-shadow p-8">
          <div className="flex flex-col items-center mb-8">
            <div className="w-16 h-16 bg-[#5D1822] rounded-full flex items-center justify-center mb-4">
              {mode === '2fa' || mode === 'verify-email' || mode === 'verifying-email-link' ? (
                <ShieldCheck className="w-8 h-8 text-[#C5A059]" />
              ) : mode === 'forgot' || mode === 'reset' ? (
                <KeyRound className="w-8 h-8 text-[#C5A059]" />
              ) : (
                <Beef className="w-8 h-8 text-[#C5A059]" />
              )}
            </div>
            <h2 className="text-3xl font-serif font-bold text-[#5D1822] text-center">
              Macelleria Borgese
            </h2>
            <p className="text-[#5A5A5A] text-center mt-2">{subtitle}</p>
          </div>

          {(isLogin || isRegister) && (
            <form onSubmit={handleAuthSubmit} className="space-y-4">
              {isRegister && (
                <div data-testid="register-full-name-field">
                  <label htmlFor="full_name" className="block text-sm font-medium text-[#2C2C2C] mb-2">
                    Nome Completo
                  </label>
                  <input
                    type="text"
                    id="full_name"
                    name="full_name"
                    value={formData.full_name}
                    onChange={handleChange}
                    className="input-default w-full"
                    required
                    data-testid="full-name-input"
                  />
                </div>
              )}

              <div data-testid="email-field">
                <label htmlFor="email" className="block text-sm font-medium text-[#2C2C2C] mb-2">
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="input-default w-full"
                  autoComplete="email"
                  required
                  data-testid="email-input"
                />
              </div>

              {isRegister && (
                <div data-testid="register-phone-field">
                  <label htmlFor="phone" className="block text-sm font-medium text-[#2C2C2C] mb-2">
                    Telefono (Opzionale)
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="input-default w-full"
                    autoComplete="tel"
                    data-testid="phone-input"
                  />
                </div>
              )}

              <div data-testid="password-field">
                <div className="flex items-center justify-between mb-2">
                  <label htmlFor="password" className="block text-sm font-medium text-[#2C2C2C]">
                    Password
                  </label>
                  {isLogin && (
                    <button
                      type="button"
                      onClick={() => {
                        setForgotEmail(formData.email);
                        setMode('forgot');
                      }}
                      className="text-sm text-[#5D1822] hover:text-[#3A0F15] font-medium"
                    >
                      Password dimenticata?
                    </button>
                  )}
                </div>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    className="input-default w-full pr-10"
                    autoComplete={isLogin ? 'current-password' : 'new-password'}
                    required
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((current) => !current)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5A5A5A] hover:text-[#5D1822]"
                    aria-label={showPassword ? 'Nascondi password' : 'Mostra password'}
                    data-testid="toggle-password-btn"
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full mt-6 disabled:opacity-50"
                data-testid="submit-btn"
              >
                {loading ? 'Caricamento...' : (isLogin ? 'Accedi' : 'Registrati')}
              </button>
            </form>
          )}

          {mode === 'login-no-email' && (
            <form onSubmit={handleNoEmailLogin} className="space-y-4">
              <div>
                <label htmlFor="no-email-name" className="block text-sm font-medium text-[#2C2C2C] mb-2">
                  Nome e cognome
                </label>
                <input
                  id="no-email-name"
                  type="text"
                  value={noEmailLogin.full_name}
                  onChange={(event) => setNoEmailLogin((current) => ({ ...current, full_name: event.target.value }))}
                  className="input-default w-full"
                  autoComplete="name"
                  required
                  autoFocus
                />
              </div>
              <div>
                <label htmlFor="no-email-phone" className="block text-sm font-medium text-[#2C2C2C] mb-2">
                  Numero di telefono
                </label>
                <input
                  id="no-email-phone"
                  type="tel"
                  value={noEmailLogin.phone}
                  onChange={(event) => setNoEmailLogin((current) => ({ ...current, phone: event.target.value }))}
                  className="input-default w-full"
                  autoComplete="tel"
                  required
                />
              </div>
              <div>
                <label htmlFor="no-email-password" className="block text-sm font-medium text-[#2C2C2C] mb-2">
                  Password ricevuta dalla macelleria
                </label>
                <div className="relative">
                  <input
                    id="no-email-password"
                    type={showPassword ? 'text' : 'password'}
                    value={noEmailLogin.password}
                    onChange={(event) => setNoEmailLogin((current) => ({ ...current, password: event.target.value }))}
                    className="input-default w-full pr-10"
                    autoComplete="current-password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((current) => !current)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5A5A5A] hover:text-[#5D1822]"
                    aria-label={showPassword ? 'Nascondi password' : 'Mostra password'}
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>
              <button type="submit" disabled={loading} className="btn-primary w-full disabled:opacity-50">
                {loading ? 'Caricamento...' : 'Accedi senza email'}
              </button>
              <p className="text-xs text-center text-[#5A5A5A]">
                Per recuperare o cambiare questa password, contatta la macelleria.
              </p>
              <button type="button" onClick={backToLogin} className="w-full flex items-center justify-center gap-2 text-[#5D1822] font-medium">
                <ArrowLeft size={18} /> Torna all'accesso con email
              </button>
            </form>
          )}

          {mode === 'verify-email' && (
            <form onSubmit={handleVerifyRegistrationEmail} className="space-y-4">
              <div className="rounded-xl bg-[#F9F7F2] p-4 text-sm text-[#5A5A5A]">
                Abbiamo inviato un codice di 6 cifre a <strong>{emailVerification.contactHint}</strong>.
                Inseriscilo qui sotto per completare la registrazione.
              </div>
              <div>
                <label htmlFor="registration-email-code" className="block text-sm font-medium text-[#2C2C2C] mb-2">
                  Codice di verifica
                </label>
                <input
                  id="registration-email-code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={emailVerification.code}
                  onChange={(event) => setEmailVerification((current) => ({
                    ...current,
                    code: event.target.value.replace(/\D/g, '').slice(0, 6)
                  }))}
                  className="input-default w-full text-center text-2xl tracking-[0.35em] font-mono"
                  maxLength={6}
                  autoFocus
                  required
                />
              </div>
              <button
                type="submit"
                disabled={loading || emailVerification.code.length !== 6}
                className="btn-primary w-full disabled:opacity-50"
              >
                {loading ? 'Verifica...' : 'Verifica e Accedi'}
              </button>
              <button
                type="button"
                onClick={handleResendRegistrationCode}
                disabled={loading}
                className="w-full text-[#5D1822] font-medium disabled:opacity-50"
              >
                Non hai ricevuto il codice? Reinvia
              </button>
              <button type="button" onClick={backToLogin} className="w-full flex items-center justify-center gap-2 text-[#5D1822] font-medium">
                <ArrowLeft size={18} /> Torna al login
              </button>
            </form>
          )}

          {mode === 'verifying-email-link' && (
            <div className="rounded-xl bg-[#F9F7F2] p-5 text-center text-[#5A5A5A]">
              Verifica dell'email in corso...
            </div>
          )}

          {mode === '2fa' && (
            <form onSubmit={handleVerify2FA} className="space-y-4">
              <div className="rounded-xl bg-[#F9F7F2] p-4 text-sm text-[#5A5A5A]">
                Abbiamo inviato un codice via {twoFA.method === 'sms' ? 'SMS' : 'email'} a{' '}
                <strong>{twoFA.contactHint || 'contatto configurato'}</strong>.
              </div>
              <div>
                <label htmlFor="two-factor-code" className="block text-sm font-medium text-[#2C2C2C] mb-2">
                  Codice di verifica
                </label>
                <input
                  id="two-factor-code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={twoFA.code}
                  onChange={(event) => setTwoFA((current) => ({
                    ...current,
                    code: event.target.value.replace(/\D/g, '').slice(0, 6)
                  }))}
                  className="input-default w-full text-center text-2xl tracking-[0.35em] font-mono"
                  maxLength={6}
                  autoFocus
                  required
                />
              </div>
              <button type="submit" disabled={loading || twoFA.code.length !== 6} className="btn-primary w-full disabled:opacity-50">
                {loading ? 'Verifica...' : 'Verifica e Accedi'}
              </button>
              <button type="button" onClick={backToLogin} className="w-full flex items-center justify-center gap-2 text-[#5D1822] font-medium">
                <ArrowLeft size={18} /> Torna al login
              </button>
            </form>
          )}

          {mode === 'forgot' && (
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div>
                <label htmlFor="forgot-email" className="block text-sm font-medium text-[#2C2C2C] mb-2">
                  Email del tuo account
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5A5A5A]" size={19} />
                  <input
                    id="forgot-email"
                    type="email"
                    value={forgotEmail}
                    onChange={(event) => setForgotEmail(event.target.value)}
                    className="input-default w-full pl-10"
                    autoComplete="email"
                    required
                    autoFocus
                  />
                </div>
              </div>
              <button type="submit" disabled={loading} className="btn-primary w-full disabled:opacity-50">
                {loading ? 'Invio...' : 'Invia link di recupero'}
              </button>
              <button type="button" onClick={backToLogin} className="w-full flex items-center justify-center gap-2 text-[#5D1822] font-medium">
                <ArrowLeft size={18} /> Torna al login
              </button>
            </form>
          )}

          {mode === 'reset' && (
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div>
                <label htmlFor="new-password" className="block text-sm font-medium text-[#2C2C2C] mb-2">
                  Nuova password
                </label>
                <div className="relative">
                  <input
                    id="new-password"
                    type={showNewPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(event) => setNewPassword(event.target.value)}
                    className="input-default w-full pr-10"
                    autoComplete="new-password"
                    minLength={8}
                    required
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword((current) => !current)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5A5A5A] hover:text-[#5D1822]"
                    aria-label={showNewPassword ? 'Nascondi password' : 'Mostra password'}
                  >
                    {showNewPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>
              <div>
                <label htmlFor="confirm-password" className="block text-sm font-medium text-[#2C2C2C] mb-2">
                  Conferma nuova password
                </label>
                <input
                  id="confirm-password"
                  type={showNewPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(event) => setConfirmPassword(event.target.value)}
                  className="input-default w-full"
                  autoComplete="new-password"
                  minLength={8}
                  required
                />
              </div>
              <p className="text-xs text-[#5A5A5A]">La password deve contenere almeno 8 caratteri.</p>
              <button type="submit" disabled={loading} className="btn-primary w-full disabled:opacity-50">
                {loading ? 'Salvataggio...' : 'Salva nuova password'}
              </button>
              <button type="button" onClick={backToLogin} className="w-full flex items-center justify-center gap-2 text-[#5D1822] font-medium">
                <ArrowLeft size={18} /> Torna al login
              </button>
            </form>
          )}

          {isLogin && (
            <div className="mt-5">
              <button
                type="button"
                onClick={() => setMode('login-no-email')}
                className="w-full py-3 rounded-xl border border-[#5D1822]/25 text-[#5D1822] font-semibold hover:bg-[#5D1822]/5 transition-colors"
              >
                Non hai un'email? Accedi con nome e telefono
              </button>
            </div>
          )}

          {(isLogin || isRegister) && (
            <div className="mt-6 text-center">
              <button
                type="button"
                onClick={() => setMode(isLogin ? 'register' : 'login')}
                className="text-[#5D1822] hover:text-[#3A0F15] font-medium"
                data-testid="toggle-auth-mode-btn"
              >
                {isLogin ? 'Non hai un account? Registrati' : 'Hai già un account? Accedi'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
